import React, { useState, useRef, Fragment } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, TagIcon, ChevronDownIcon, ExternalLinkIcon, NewspaperIcon, TrophyIcon, InstagramIcon, FacebookIcon, PlayCircleIcon, XIcon, Share2Icon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost, SPORTS_COMPOSITE_IMAGES } from '../data/blog-posts';
import { getCategoryColor, getPostNum, resolveTranslatedField, translateCategory } from '../utils/blog-helpers';
import { StoryBody, stripImageMarkers } from './shared/StoryBody';
// ============================================================
//  Blog posts data is now in data/blog-posts.ts
//  Edit that file to add or modify blog posts.
// ============================================================
const ALL_CATEGORIES = ['All', 'Political', 'Immigration', 'Business', 'Note Worthy', 'Events', 'Arts & Entertainment', 'Sports'];
const GRADIENT_FALLBACKS: Record<string, string> = {
  Political: 'from-haa-blue via-blue-700 to-haa-navy',
  Immigration: 'from-haa-skyblue to-haa-midblue',
  Business: 'from-emerald-500 to-emerald-800',
  'Note Worthy': 'from-haa-midblue to-haa-navy',
  'Arts & Entertainment': 'from-haa-navy to-blue-950',
  Events: 'from-blue-400 to-haa-midblue',
  Sports: 'from-orange-500 to-red-600'
};
function getGradient(category: string) {
  return GRADIENT_FALLBACKS[category] ?? 'from-haa-blue to-haa-navy';
}
// Story Modal Component
function StoryModal({
  isOpen,
  onClose,
  post,
  translatedTitle,
  translatedDate,
  translatedFullStory,
  isEs,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost;
  translatedTitle: string;
  translatedDate: string;
  translatedFullStory?: string;
  isEs: boolean;
  t: any;
}) {
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  return <AnimatePresence data-id="element-3093" data-mp="37n81ii0f6ej2">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-3094" data-mp="3fofltzaiu4s5">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-3095" data-mp="2w0y5rgmz13sj">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-3096" data-mp="2amwkr01p4926">
              <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3097" data-mp="20qjru4dz5z8h">
                {post.category}
              </span>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-3098" data-mp="1rr6ev1qn6sj0">
                <XIcon className="w-5 h-5" data-id="element-3099" data-mp="1hyh8gl9y84uj" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" data-id="element-3100" data-mp="1cznuysunwdud">
              {post.image && !(post.players && post.players.length > 0) && <div className="w-full h-80 sm:h-96 relative" data-id="element-3101" data-mp="140al3z2mln94">
                  <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover" style={{
              objectPosition: post.imagePosition || 'center top'
            }} data-id="element-3102" data-mp="1436gpgr03zue" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-3103" data-mp="1o0qbtgcqrfj8" />
                </div>}

              {/* Video Embeds — shown at top when no image */}
              {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="w-full" data-id="element-3104" data-mp="110sgfineof0s">
                    {post.videoEmbeds.map((video, vi) => <div key={vi} className="bg-black relative" data-id="element-3105" data-mp="151cgov39zyr5" data-mpi={vi}>
                        {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-3106" data-mp="2vfczjqw2w2i2">
                            <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-3107" data-mp="nqgy1b1zsb1z" />
                            {/* Fallback thumbnail link */}
                            <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-3108" data-mp="3iaogyuc0xuf0">
                              <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-3109" data-mp="1r3r05ks99dt9" />
                              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-3110" data-mp="252qlojkh1cl7">
                                <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-3111" data-mp="29x4jesmlvvt1" />
                                <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-3112" data-mp="2evxp3yvniiin">
                                  Watch on YouTube
                                </span>
                              </div>
                            </a>
                          </div> : null}
                      </div>)}
                  </div>}

              <div className="p-6 sm:p-8" data-id="element-3113" data-mp="35zod27nef0l">
                <div className="flex items-center gap-2 mb-4" data-id="element-3114" data-mp="38vkqm3ukvmx">
                  <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-3115" data-mp="3ue40qqy7uo69" />
                  <span className="font-nunito text-gray-500 text-sm font-semibold" data-id="element-3116" data-mp="uxp8r60hrd72">
                    {translatedDate}
                  </span>
                </div>

                <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-6" data-id="element-3117" data-mp="qyge7yxmnbag">
                  {translatedTitle}
                </h2>

                {translatedFullStory && <StoryBody text={translatedFullStory} className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line mb-8" data-id="element-3118" data-mp="1elxzrcqatd4z" />}

                {/* Video Embeds — shown after story when post has image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8 space-y-4" data-id="element-3119" data-mp="8aibyoomqk2d">
                      {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black relative" data-id="element-3120" data-mp="d93bsmzyz1qa" data-mpi={vi}>
                          {video.youtubeId ? <div className="relative w-full" style={{
                  paddingBottom: '56.25%'
                }} data-id="element-3121" data-mp="15uv3f6fwlol7">
                              <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                    border: 0
                  }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-3122" data-mp="3cmm3icydjbxv" />
                              <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-3123" data-mp="304t8ylj3m5hi">
                                <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-3124" data-mp="22nusg0ek0ihq" />
                                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-3125" data-mp="26l6zjyszupkq">
                                  <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-3126" data-mp="21mvq3p6m2h31" />
                                  <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-3127" data-mp="1wu2jswvhv0ou">
                                    Watch on YouTube
                                  </span>
                                </div>
                              </a>
                            </div> : null}
                        </div>)}
                    </div>}

                {/* Story Images Gallery */}
                {post.storyImages && post.storyImages.length > 0 && <div className="mb-8 space-y-4" data-id="element-3128" data-mp="18qw62lndffvm">
                    {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-3129" data-mp="1drjxv9jfc0qw" data-mpi={idx}>
                        <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-3130" data-mp="l1uwuw6ps0pa" />
                      </div>)}
                  </div>}

                {/* Players */}
                {post.players && post.players.length > 0 && <div className="space-y-3 mb-8" data-id="element-3131" data-mp="2bljcq5h8mia">
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-3132" data-mp="2atxw85lg55q">
                      {isEs ? 'Jugadores Destacados' : 'Featured Players'}
                    </h3>
                    {post.players.map(player => {
                const isExpanded = expandedPlayer === player.name;
                const playerKeyMap: Record<string, string> = {
                  'Oscar Avilez': 'avilez',
                  'David Ruíz': 'ruiz',
                  'Joseph Rosales': 'rosales',
                  'Andy Nájar': 'najar',
                  'Bryan Acosta': 'acosta'
                };
                const playerKey = playerKeyMap[player.name] ?? '';
                const translatedBio = playerKey ? t(`news.player.${playerKey}.bio`) : player.bio;
                const translatedPosition = player.position === 'Midfielder' ? t('news.player.position.midfielder') : player.position === 'Defender' ? t('news.player.position.defender') : player.position;
                return <div key={player.name} className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50" data-id="element-3133" data-mp="197gq6dw32wg0">
                          <button onClick={() => setExpandedPlayer(isExpanded ? null : player.name)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-100 transition-colors text-left" aria-expanded={isExpanded} data-id="element-3134" data-mp="z9avohclwsl6">
                            {player.image ? <img src={player.image} alt={player.name} className="w-16 h-16 rounded-full object-cover object-top shadow-md flex-shrink-0 border-2 border-white" data-id="element-3135" data-mp="1i6vjdl3mxl47" /> : <div className={`w-16 h-16 rounded-full ${player.teamColor} flex items-center justify-center text-white font-nunito font-bold text-lg shadow-md flex-shrink-0`} data-id="element-3136" data-mp="i1b7rkol8tmk">
                                {player.name.split(' ').map(n => n[0]).join('')}
                              </div>}
                            <div className="flex-1 min-w-0" data-id="element-3137" data-mp="w0ugaelba6ze">
                              <p className="font-nunito text-sm font-bold text-haa-navy leading-tight" data-id="element-3138" data-mp="vz9mir6tms2l">
                                {player.name}
                                <span className="text-gray-400 font-normal ml-1.5" data-id="element-3139" data-mp="1ohwwpbka4mow">
                                  #{player.number}
                                </span>
                              </p>
                              <p className="font-nunito text-xs text-gray-500 mt-0.5" data-id="element-3140" data-mp="32e6rpsdycbre">
                                {translatedPosition} · {player.team}
                              </p>
                            </div>
                            <motion.span animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex-shrink-0" data-id="element-3141" data-mp="3bcjr5dwpjp8j">
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-3142" data-mp="3v3dn4ii40l8f" />
                            </motion.span>
                          </button>
                          <AnimatePresence initial={false} data-id="element-3143" data-mp="7xe8o4p0vuv7">
                            {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.25,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="overflow-hidden" data-id="element-3144" data-mp="cw7nc0g1prwy">
                                <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-3145" data-mp="2wqhw5zoqv8je">
                                  <p className="font-nunito text-sm text-gray-600 leading-relaxed" data-id="element-3146" data-mp="17bbcm6rydryo">
                                    {translatedBio}
                                  </p>
                                  <a href={player.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue transition-colors" data-id="element-3147" data-mp="3dy91nwul4q5r">
                                    {t('news.player.viewProfile')}
                                    <ExternalLinkIcon className="w-3 h-3" data-id="element-3148" data-mp="307ngg1hns44p" />
                                  </a>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>}

                {/* Video Embeds — shown after story when there is an image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8" data-id="element-3149" data-mp="33ej073e6vkkp">
                      <div className="flex items-center gap-2 mb-4" data-id="element-3150" data-mp="33fu4r5fgkwcv">
                        <PlayCircleIcon className="w-5 h-5 text-amber-600" data-id="element-3151" data-mp="2yn9daak0yvci" />
                        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider" data-id="element-3152" data-mp="2ylg8l0sz2l75">
                          {isEs ? 'Videos' : 'Videos'}
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3153" data-mp="2ko1brb0e3b55">
                        {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black" data-id="element-3154" data-mp="2fpzsaoz1x9t5" data-mpi={vi}>
                            {video.youtubeId ? <div className="relative w-full" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-3155" data-mp="dvhbxnhdnyju">
                                <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-3156" data-mp="lg1e6lhsjkl8" />
                              </div> : video.facebookUrl ? <a href={video.facebookUrl} target="_blank" rel="noopener noreferrer" className="relative block w-full bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 transition-colors" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-3157" data-mp="cg4vr1lzvhym">
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" data-id="element-3158" data-mp="380r381ojkflv">
                                  <FacebookIcon className="w-8 h-8 text-blue-400" data-id="element-3159" data-mp="3c0rtcjl06f8r" />
                                  <span className="font-nunito text-white text-sm font-semibold" data-id="element-3160" data-mp="2fc2aapxn3gl7">
                                    {video.label}
                                  </span>
                                  <span className="font-nunito text-white/50 text-xs" data-id="element-3161" data-mp="2obfkxlxi2b7i">
                                    {isEs ? 'Ver en Facebook' : 'Watch on Facebook'}
                                  </span>
                                </div>
                              </a> : null}
                            <div className="px-4 py-3 bg-gray-900" data-id="element-3162" data-mp="iord7c7jvfux">
                              <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-3163" data-mp="dperiusf0etx">
                                {video.label}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {/* Social Links */}
                {post.socialLinks && post.socialLinks.length > 0 && <div className="mb-8 pt-6 border-t border-gray-100" data-id="element-3164" data-mp="3r3jjfosow8vb">
                    <h3 className="font-bebas text-xl text-haa-navy tracking-wider mb-4" data-id="element-3165" data-mp="3m4opbqp9uxvw">
                      {isEs ? 'Síguelos' : 'Follow Them'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-3166" data-mp="1kdv4anwilogi">
                      {post.socialLinks.map((social, si) => <a key={si} href={social.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito text-sm font-bold transition-all duration-200 ${social.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white hover:shadow-lg hover:shadow-pink-500/25 hover:scale-105' : 'bg-[#1877F2] text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105'}`} aria-label={social.label} data-id="element-3167" data-mp="1gc01feyxr30m" data-mpi={si}>
                          {social.platform === 'instagram' ? <InstagramIcon className="w-4 h-4" data-id="element-3168" data-mp="3jn4sjhjmzkx1" /> : <FacebookIcon className="w-4 h-4" data-id="element-3169" data-mp="3olzid3g0u9qn" />}
                          {social.label}
                        </a>)}
                    </div>
                  </div>}

                {/* Coverage Links */}
                {post.coverageLinks && post.coverageLinks.length > 0 && <div className="pt-6 border-t border-gray-100" data-id="element-3170" data-mp="20lc9szvaysb4">
                    <div className="flex items-center gap-2 mb-3" data-id="element-3171" data-mp="2emezdmliakqs">
                      <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-3172" data-mp="2ejj1fy9nrw2c" />
                      <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-3173" data-mp="29nbrlue560f4">
                        {t('news.alsoCoveredBy')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-3174" data-mp="1gzy0we11e13g">
                      {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200" data-id="element-3175" data-mp="1qwknb790h07l" data-mpi={i}>
                          {link.label}
                          <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3176" data-mp="agyaknlh1l61" />
                        </a>)}
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// Featured Card
function FeaturedCard({
  post
}: {
  post: BlogPost;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedExcerpt = resolveTranslatedField(t, postNum, 'excerpt', post.excerpt);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const hasStoryUrl = post.storyUrl && post.storyUrl !== '#';
  const hasFullStory = !!post.fullStory || !!post.players || !!post.videoEmbeds;
  const translatedCategory = translateCategory(t, post.category);
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-xl lg:col-span-2 group" data-id="element-3177" data-mp="3bcpa660ik4eu">
      <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-3178" data-mp="1qyyq3a6ncww1">
        {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition || 'center 25%'
      }} data-id="element-3179" data-mp="1lxhthqv73wl9" /> : !post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && post.videoEmbeds[0].youtubeId ? <iframe src={`https://www.youtube.com/embed/${post.videoEmbeds[0].youtubeId}`} title={post.videoEmbeds[0].label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-3180" data-mp="1ii7s1rtwlody" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3181" data-mp="zqx5tnz4hzse">
            <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 30% 70%, white 1px, transparent 1px)',
          backgroundSize: '25px 25px'
        }} data-id="element-3182" data-mp="lqmdegi60d54" />
          </div>}
        {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-3183" data-mp="g4veemz57pck" />}
        <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-3184" data-mp="c171rpvnm3kg">
          <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)} mb-3 inline-block`} data-id="element-3185" data-mp="q1hy38fqgad0">
            {translatedCategory}
          </span>
          {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <p className="font-bebas text-white text-3xl sm:text-4xl leading-tight tracking-wider line-clamp-2" data-id="element-3186" data-mp="18sr77gllcx42">
              {translatedTitle}
            </p>}
        </div>
      </div>

      <div className="p-6 sm:p-8" data-id="element-3187" data-mp="2b63xmty606t7">
        <div className="flex items-center gap-3 mb-4" data-id="element-3188" data-mp="2b52b9tbp7xw6">
          <div className="w-8 h-1 bg-haa-blue rounded-full" data-id="element-3189" data-mp="2b8ggrafnsn7u" />
          <span className="font-nunito text-haa-blue text-xs font-bold uppercase tracking-widest" data-id="element-3190" data-mp="2g3nb3rn1gfk3">
            {t('news.featuredStory')}
          </span>
        </div>

        <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-3" data-id="element-3191" data-mp="2p64zocq61ka9">
          {translatedTitle}
        </h2>

        <div className="mb-6" data-id="element-3192" data-mp="1shgrs985zdek">
          <p className="font-nunito text-gray-600 leading-relaxed" data-id="element-3193" data-mp="1wgy15dqpoup0">
            {translatedExcerpt}
          </p>

          {/* Story preview — show first 2 paragraphs of fullStory */}
          {translatedFullStory && <div className="relative mt-4" data-id="element-3194" data-mp="267ahsuveo3pv">
              <div className="font-nunito text-gray-500 text-sm leading-relaxed space-y-3" data-id="element-3195" data-mp="2693r6hxel21k">
                {stripImageMarkers(translatedFullStory).split('\n\n').slice(0, 2).map((para, i) => <p key={i} data-id="element-3196" data-mp="167gqz91zahmx" data-mpi={i}>{para}</p>)}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-3197" data-mp="118dq80t16xkr" />
            </div>}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5" data-id="element-3198" data-mp="wb42cplaitz1">
            {hasStoryUrl ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-3199" data-mp="s9s7o5nk7ijz">
                {t('news.readFullStory')}
                <ExternalLinkIcon className="w-4 h-4" data-id="element-3200" data-mp="8hdipfvhrboc" />
              </a> : hasFullStory ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-3201" data-mp="4jo5m2q0rqv3">
                {t('news.readFullStory')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-3202" data-mp="3el2fx0m8ow9b" />
              </button> : null}

            {post.coverageLinks && post.coverageLinks.length > 0 && <>
                <span className="text-gray-200 select-none hidden sm:inline" data-id="element-3203" data-mp="2lvls6g9ke3jf">
                  |
                </span>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-3204" data-mp="2gxk23bdq13yg">
                  <NewspaperIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-3205" data-mp="2vlon4m08xrke" />
                  <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3206" data-mp="2vnhvq6uf6lv1">
                    {t('news.alsoCoveredBy')}
                  </span>
                  {post.coverageLinks.map((link, i) => <Fragment key={link.url} data-id="element-3207" data-mp="1y72ipxes3kpr" data-mpi={i}>
                      {i > 0 && <span className="text-gray-200 select-none" data-id="element-3208" data-mp="dnqfxby7bzuq">·</span>}
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3209" data-mp="ifguxv833bsh">
                        {link.label}
                        <ExternalLinkIcon className="w-3 h-3" data-id="element-3210" data-mp="11dt7lqy7f3st" />
                      </a>
                    </Fragment>)}
                </div>
              </>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-3211" data-mp="rf3uebh967iw">
          <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm" data-id="element-3212" data-mp="384ahsvjz6p9x">
            <span className="flex items-center gap-1.5" data-id="element-3213" data-mp="386n75ts2c1x0">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-3214" data-mp="3c6nktc7c8930" />
              <span className="font-nunito" data-id="element-3215" data-mp="1imvoa8ztkvgb">{translatedDate}</span>
            </span>
          </div>
          <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1.5 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3216" data-mp="1doc4ae4sdtql">
            <Share2Icon className="w-3.5 h-3.5" data-id="element-3217" data-mp="zwnr07iswz81" />
            {isEs ? 'Compartir' : 'Share'}
          </a>
        </div>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3218" data-mp="uxb4jnvudu5z" />
    </motion.article>;
}
// Regular Card
function PostCard({
  post,
  index
}: {
  post: BlogPost;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedExcerpt = resolveTranslatedField(t, postNum, 'excerpt', post.excerpt);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedReadTime = resolveTranslatedField(t, postNum, 'readTime', post.readTime);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const translatedCategory = translateCategory(t, post.category);
  const isSportsComposite = post.id === 'post-8' || post.players && post.players.length > 0;
  const isWabafu = post.id === 'post-12';
  const compositeImages = SPORTS_COMPOSITE_IMAGES;
  const hasMultipleImages = !!post.secondImage;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-lg group flex flex-col" data-id="element-3219" data-mp="171jbz35awmpi">
      {isSportsComposite ? <div className="relative h-64 overflow-hidden flex-shrink-0 bg-gradient-to-b from-haa-midblue via-haa-blue to-haa-navy" data-id="element-3220" data-mp="1pysxcpu6sa9e">
          {/* Player Images */}
          <div className="absolute inset-0 flex items-end justify-center px-2 pb-12 sm:pb-10" data-id="element-3221" data-mp="1ys5yug6bgk73">
            {compositeImages.map((img, i) => <motion.div key={img.name} initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + i * 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-1/5 -mx-1.5 sm:-mx-2" data-id="element-3222" data-mp="1utzvli4osmqx" data-mpi={i}>
                <img src={img.url} alt={img.name} className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-3223" data-mp="24nm8xwwhqsr5" />
              </motion.div>)}
          </div>
          {/* Gradient Overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-haa-navy/80 to-transparent" data-id="element-3224" data-mp="1ur9g118rsixg" />
          {/* Top Left Badge */}
          <div className="absolute top-3 left-3" data-id="element-3225" data-mp="1lo8ab1ntgtub">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3226" data-mp="1lpjolvew1jzo">
              {translatedCategory}
            </span>
          </div>
          {/* Bottom Text */}
          <div className="absolute bottom-0 left-0 right-0 p-5 pb-3" data-id="element-3227" data-mp="3nl8z6b1c26mq">
            <p className="text-white/90 font-nunito text-[10px] sm:text-xs font-bold tracking-widest mb-1 flex items-center gap-1.5" data-id="element-3228" data-mp="3imo0wh5n5x0x">
              <span className="text-lg leading-none" data-id="element-3229" data-mp="3dpfeg4dgn6tv">🇭🇳</span>
              {isEs ? 'HONDUREÑOS EN LA MLS · TEMPORADA 2025-26' : 'HONDURANS IN MLS · 2025-26 SEASON'}
            </p>
          </div>
        </div> : isWabafu ? <div className="relative h-52 overflow-hidden flex-shrink-0" data-id="element-3230" data-mp="57rop8h7zrok">
          <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center center'
      }} data-id="element-3231" data-mp="5a5kxknmz9xp" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-3232" data-mp="2lzax6ekzyzwv" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-3233" data-mp="15ghdmys8zcsf">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={inView ? {
          scale: 1,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-3234" data-mp="1afua8b8x7y87">
              <span className="text-5xl mb-3 block" data-id="element-3235" data-mp="1t6lfp1psf4hb">🥁</span>
              <p className="font-bebas text-white text-2xl sm:text-3xl tracking-wider leading-tight" data-id="element-3236" data-mp="1y5omaqlf1ire">
                {isEs ? 'CULTURA EN MOVIMIENTO' : 'CULTURE IN MOTION'}
              </p>
              <p className="font-nunito text-white/70 text-xs tracking-widest uppercase mt-1" data-id="element-3237" data-mp="3qvqaqgmepyx">
                Wabafu Garifuna Dance Theater
              </p>
            </motion.div>
          </div>
          <div className="absolute top-3 left-3" data-id="element-3238" data-mp="21nc9g7vhzqtl">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3239" data-mp="21mkodqzj1bhk">
              {translatedCategory}
            </span>
          </div>
        </div> : hasMultipleImages ? <div className="flex-shrink-0" data-id="element-3240" data-mp="1mql9nxhwwd3g">
          <div className="grid grid-cols-2 gap-0.5" data-id="element-3241" data-mp="1mrmu2sktibxu">
            <div className="relative h-36 overflow-hidden" data-id="element-3242" data-mp="1ipjim3y5f7ck">
              <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: post.imagePosition ?? 'center 15%'
          }} data-id="element-3243" data-mp="1wnpzq8evospj" />
            </div>
            <div className="relative h-36 overflow-hidden" data-id="element-3244" data-mp="333jsond28m12">
              <img src={post.secondImage!} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: 'center center'
          }} data-id="element-3245" data-mp="ovsp024hhfda" />
            </div>
          </div>
          <div className="relative -mt-8 px-3 pb-0 z-10" data-id="element-3246" data-mp="kuyyr23kjoh8">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3247" data-mp="18k1jwjxevzgh">
              {translatedCategory}
            </span>
          </div>
        </div> : <div className="relative h-44 overflow-hidden flex-shrink-0" data-id="element-3248" data-mp="13tl0mj6wtjm7">
          {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center 15%'
      }} data-id="element-3249" data-mp="1rj5kr5esumx2" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3250" data-mp="1r3dg0anlyxt1">
              <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-3251" data-mp="1d45db7s8l7k3" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-3252" data-mp="29uo30gq7qqfn" />
          <div className="absolute top-3 left-3" data-id="element-3253" data-mp="1vzc7i748gvxo">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3254" data-mp="201yunupylsoj">
              {translatedCategory}
            </span>
          </div>
        </div>}

      <div className="p-5 flex flex-col flex-1" data-id="element-3255" data-mp="h4ccm3v9tl41">
        <div className="flex items-center gap-2 mb-2" data-id="element-3256" data-mp="h31821u049bv">
          <div className="w-4 h-0.5 bg-haa-blue rounded-full" data-id="element-3257" data-mp="c5iws4sihi0p" />
          <span className="font-nunito text-gray-400 text-xs" data-id="element-3258" data-mp="c4zhywy5r9qw">
            {translatedDate}
          </span>
        </div>

        {!isSportsComposite && <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight mb-2" data-id="element-3259" data-mp="lustjxyavov5">
            {translatedTitle}
          </h3>}

        <div className="flex-1 mb-3" data-id="element-3260" data-mp="3ucypojax2ym3">
          {!isSportsComposite && <p className="font-nunito text-gray-500 text-sm leading-relaxed line-clamp-3" data-id="element-3261" data-mp="3pdcba6jcrcbp">
              {translatedExcerpt}
            </p>}
        </div>

        <div className="mb-4" data-id="element-3262" data-mp="34bc4pix4fp3">
          {post.storyUrl && post.storyUrl !== '#' ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3263" data-mp="1jib9ptuajt96">
              {t('news.readFullStory')}
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3264" data-mp="1vkkjx1cf6j7k" />
            </a> : post.fullStory || post.players || post.videoEmbeds ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3265" data-mp="20jfgd63b86a5">
              {t('news.readFullStory')}
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3266" data-mp="1hlurw04gj9q2" />
            </button> : null}
        </div>

        {post.coverageLinks && post.coverageLinks.length > 0 && <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3" data-id="element-3267" data-mp="p51ug6s4a0wr">
            <NewspaperIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-3268" data-mp="p2xid1k2dvle" />
            <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3269" data-mp="32erv1w60lri8">
              {t('news.alsoCoveredBy')}
            </span>
            {post.coverageLinks.map((link, i) => <Fragment key={i} data-id="element-3270" data-mp="2tcsgdaih2zig" data-mpi={i}>
                {i > 0 && <span className="text-gray-200 select-none" data-id="element-3271" data-mp="2og1xygj73rtv">·</span>}
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3272" data-mp="2ohwjnx2ajnz0">
                  {link.label}
                  <ExternalLinkIcon className="w-3 h-3" data-id="element-3273" data-mp="25pu1uidwy1n6" />
                </a>
              </Fragment>)}
          </div>}
      </div>

      <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-gray-100 mt-auto" data-id="element-3274" data-mp="20s2bvtwtwplm">
        <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs" data-id="element-3275" data-mp="1vt7i5wxbpgmq">
          <span className="flex items-center gap-1" data-id="element-3276" data-mp="3vv7capsxgsdp">
            <CalendarIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-3277" data-mp="4n2x6taz654c" />
            <span className="font-nunito" data-id="element-3278" data-mp="3m3b1qf9nn9sl">{translatedDate}</span>
          </span>
        </div>
        <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3279" data-mp="3m1gnfjsw2fxh">
          <Share2Icon className="w-3.5 h-3.5" data-id="element-3280" data-mp="3i30vr2lv4jdq" />
          {isEs ? 'Compartir' : 'Share'}
        </a>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3281" data-mp="336jmcgx8hett" />
    </motion.article>;
}
// Main Section
export function NewsBlog() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    t
  } = useLanguage();
  const filtered = activeCategory === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === activeCategory);
  const featured = filtered.find(p => p.featured) ?? null;
  const rest = filtered.filter(p => p.id !== featured?.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const categoryTranslations: Record<string, string> = {
    All: t('news.catAll'),
    Political: t('news.catPolitical'),
    Immigration: t('news.catImmigration'),
    Business: t('news.catBusiness'),
    'Note Worthy': t('news.catNoteWorthy'),
    Events: t('news.catEvents'),
    'Arts & Entertainment': t('news.catArtMedia'),
    Sports: t('news.catSports')
  };
  return <section id="news" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="news-heading" data-id="element-3282" data-mp="1k72f2zk8qszf">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3283" data-mp="3k9uy1eeum9k9">
        <div ref={titleRef} className="mb-10" data-id="element-3284" data-mp="3p84udscb7g7g">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-3285" data-mp="3p8y5f5iawrve">
            <div data-id="element-3286" data-mp="gscsxeqgngnj">
              <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3287" data-mp="grtkqpqm46mp">
                {t('news.label')}
              </span>
              <h2 id="news-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3288" data-mp="psrgkkqwd7u7">
                {t('news.heading')}
              </h2>
              <div className="flex items-center gap-3 mt-4" data-id="element-3289" data-mp="14wb1gykcwv5z">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3290" data-mp="zuveh0ymceky" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3291" data-mp="uwk4mq8bq42q" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3292" data-mp="uywkmxuiu4d5" />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Filter by category" data-id="element-3293" data-mp="1nm0sdal00wsm">
          {ALL_CATEGORIES.map(cat => <button key={cat} role="tab" aria-selected={activeCategory === cat} onClick={() => setActiveCategory(cat)} className={`relative font-nunito font-semibold text-sm px-5 py-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue ${activeCategory === cat ? 'bg-haa-navy text-white shadow-md' : 'bg-white text-gray-500 hover:text-haa-navy border border-gray-200 hover:border-haa-navy'}`} data-id="element-3294" data-mp="21k7ha17yzmg5">
              <span className="inline-flex items-center gap-1.5" data-id="element-3295" data-mp="33xpjbsxv3q09">
                {cat === 'Sports' && <TrophyIcon className="w-3.5 h-3.5" data-id="element-3296" data-mp="38y3sxff4380e" />}
                {categoryTranslations[cat] ?? cat}
              </span>
            </button>)}
        </motion.div>

        <AnimatePresence mode="wait" data-id="element-3297" data-mp="2yze93h3nndhi">
          <motion.div key={activeCategory} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-3298" data-mp="2z2io79inrtmy">
            {filtered.length === 0 ? <div className="text-center py-20" data-id="element-3299" data-mp="ep3wgdxcz3ht">
                <TagIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-3300" data-mp="ensqq8kcrlo2" />
                <p className="font-nunito text-gray-400 text-lg" data-id="element-3301" data-mp="aobl9f4xeoiw">
                  {t('news.noPosts')}
                </p>
              </div> : activeCategory === 'All' ? <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3302" data-mp="3e5aas4r4z54r">
                  {featured && <FeaturedCard post={featured} data-id="element-3303" data-mp="3rvxa9z4q0z1w" />}
                  {rest.length > 0 && <div className="flex flex-col gap-6" data-id="element-3304" data-mp="3n3chn0xjwrzy">
                      {rest.slice(0, 2).map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3305" data-mp="2zd9v10ceoe8q" data-mpi={i} />)}
                    </div>}
                </div>
                {rest.length > 2 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-id="element-3306" data-mp="1sha9yhgbtbrq">
                    {rest.slice(2).map((post, i) => <PostCard key={post.id} post={post} index={i + 2} data-id="element-3307" data-mp="1jn5n094bckhh" data-mpi={i} />)}
                  </div>}
              </> : <div className={`grid gap-6 ${filtered.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`} data-id="element-3308" data-mp="1emho719uezcp">
                {filtered.map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3309" data-mp="22dwsv0p3dsr4" data-mpi={i} />)}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}